import type { FC, ReactNode } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Link } from '@confluence/route-manager/entry-points/Link.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Card = styled.div({
	position: 'relative',
	cursor: 'pointer',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface'),
	padding: token('space.200'),

	'&:hover': {
		boxShadow: token('elevation.shadow.raised'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InvisibleLink = styled(Link)({
	position: 'absolute',
	border: `${token('border.width')} solid transparent`,
	borderRadius: token('radius.large'),
	width: '100%',
	height: '100%',
	top: -1,
	left: -1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '14px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardIcon = styled.div({
	marginRight: token('space.075'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardPrimarySecondary = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 1,
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardPrimary = styled.div({
	color: token('color.text'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.xsmall'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardSecondary = styled.div({
	color: token('color.text.subtle'),
	font: token('font.body.small'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	a: {
		color: token('color.text.subtle'),
		'&:visited': {
			color: token('color.text.subtle'),
		},
		'&:hover': {
			color: token('color.text.subtle'),
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardActions = styled.div({
	marginLeft: token('space.050'),
	flexShrink: 0,
});

type NarrowPageCardLayoutProps = {
	icon?: ReactNode;
	primary: ReactNode;
	secondary?: ReactNode;
	lastModified?: ReactNode;
	actions?: ReactNode;
	className?: string;
	testId?: string;
	url?: string;
	onClick?: () => void;
	ariaLabel?: string;
};

export const NarrowPageCardLayout: FC<NarrowPageCardLayoutProps> = ({
	icon,
	primary,
	secondary,
	lastModified,
	actions,
	className,
	testId,
	url,
	onClick,
	ariaLabel,
}) => {
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<Card className={className} data-testid={testId} role="listitem">
			<InvisibleLink href={url} onClick={onClick} aria-label={ariaLabel} />
			<ContentWrapper>
				<Header>
					<CardIcon data-testid="list-item-icon">{icon}</CardIcon>
					<CardPrimarySecondary>
						<CardPrimary>{primary}</CardPrimary>
						<CardSecondary>{secondary}</CardSecondary>
					</CardPrimarySecondary>
					{actions && <CardActions>{actions}</CardActions>}
				</Header>
				<CardSecondary>{lastModified}</CardSecondary>
			</ContentWrapper>
		</Card>
	);
};
